/**
 * Shared safe formatters used by every list/detail view in the app.
 *
 * Why a shared util: every page used to define its own `formatPrice`
 * with `n: number`, which exploded with "Cannot read properties of
 * undefined (reading 'toLocaleString')" the moment an optimistic
 * offline row landed in the cache without every numeric field
 * populated. These helpers tolerate `undefined`/`null`/`NaN` and
 * return a visible placeholder so the page renders instead of
 * crashing.
 *
 * Tunisian convention: 3 decimals (millimes), `1 234,567 DT`.
 */

const PLACEHOLDER = '—';

function isFinite(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

/** Money — 3 decimals (millimes). Tolerates null/undefined/NaN. */
export function formatMoney(n: number | null | undefined): string {
  if (!isFinite(n)) return PLACEHOLDER;
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

/** Money with the DT suffix. */
export function formatMoneyDT(n: number | null | undefined): string {
  if (!isFinite(n)) return PLACEHOLDER;
  return `${formatMoney(n)} DT`;
}

/** Integer (no decimals). Tolerates null/undefined/NaN. */
export function formatInteger(n: number | null | undefined): string {
  if (!isFinite(n)) return PLACEHOLDER;
  return n.toLocaleString('fr-TN', { maximumFractionDigits: 0 });
}

/** Quantity — up to 3 decimals, no padding. */
export function formatQuantity(n: number | null | undefined): string {
  if (!isFinite(n)) return PLACEHOLDER;
  return n.toLocaleString('fr-TN', { maximumFractionDigits: 3 });
}

/** Date — DD/MM/YYYY. Tolerates null/undefined/invalid. */
export function formatDate(input: string | Date | null | undefined): string {
  if (!input) return PLACEHOLDER;
  const d = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return PLACEHOLDER;
  return d.toLocaleDateString('fr-TN', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Percentage with 2 decimals + % suffix. */
export function formatPercent(n: number | null | undefined): string {
  if (!isFinite(n)) return PLACEHOLDER;
  return `${n.toLocaleString('fr-TN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} %`;
}
